"use client";

import Link from "next/link";
import { useState } from "react";
import {
  ChevronRight,
  LayoutGrid,
  LogIn,
  Menu,
  Phone,
  Tag,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { AtriamLogo } from "@/components/branding/atriam-logo";

import type { CtaConfig } from "@/lib/features/marketing/shared/types";
import { CtaButton } from "./cta-button";

interface MarketingNavProps {
  primaryCta: CtaConfig;
}

const NAV_LINKS = [
  { label: "Features", href: "/#features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Community", href: "/community" },
];

const DRAWER_LINKS = [
  {
    label: "Features",
    href: "/#features",
    sub: "What Atriam does",
    Icon: LayoutGrid,
  },
  { label: "Pricing", href: "/pricing", sub: "Plans & pricing", Icon: Tag },
  {
    label: "Community",
    href: "/community",
    sub: "Connect with builders",
    Icon: Users,
  },
  {
    label: "Log in",
    href: "/auth/login",
    sub: "Already have an account",
    Icon: LogIn,
  },
];

export function MarketingNav({ primaryCta }: MarketingNavProps) {
  const [open, setOpen] = useState(false);

  return (
    <header className="bg-background/80 sticky top-0 z-40 w-full border-b liquid-glass-static-standard">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link
          href="/"
          aria-label="Atriam home"
          className="inline-flex min-h-11 items-center"
        >
          <AtriamLogo wordmarkClassName="text-lg" />
        </Link>
        <nav className="hidden items-center gap-6 md:flex">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-muted-foreground hover:text-foreground text-sm transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/auth/login"
            className="text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            Log in
          </Link>
          <CtaButton cta={primaryCta} size="default" />
        </nav>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" aria-label="Open navigation">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="flex w-[86%] max-w-sm flex-col gap-0 overflow-hidden border-l border-white/10 bg-[#0b0e14]/95 p-6 liquid-glass-static-elevated"
          >
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>

            {/* Ambient brand glow for depth */}
            <div
              aria-hidden
              className="bg-primary/20 pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full blur-3xl"
            />

            {/* Brand */}
            <div className="relative">
              <AtriamLogo wordmarkClassName="text-lg" />
              <p className="text-muted-foreground mt-2 text-sm">
                The AI that runs the office.
              </p>
            </div>

            {/* Nav cards */}
            <nav className="relative mt-8 flex flex-col gap-2.5">
              {DRAWER_LINKS.map(({ label, href, sub, Icon }) => (
                <Link
                  key={href}
                  href={href}
                  onClick={() => setOpen(false)}
                  className="hover:border-primary/30 group flex items-center gap-3.5 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 transition-[border-color,background-color,scale] hover:bg-white/[0.06] active:scale-[0.99]"
                >
                  <span className="bg-primary/10 text-primary ring-primary/15 group-hover:bg-primary/15 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset transition-colors">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold leading-tight">
                      {label}
                    </span>
                    <span className="text-muted-foreground block text-xs">
                      {sub}
                    </span>
                  </span>
                  <ChevronRight className="text-muted-foreground/50 group-hover:text-foreground h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </nav>

            {/* Anchored action block */}
            <div className="relative mt-auto flex flex-col gap-3 pt-8">
              <CtaButton
                cta={primaryCta}
                size="lg"
                className="shadow-primary/20 w-full rounded-xl text-base shadow-lg"
              />
              <a
                href="tel:+18582152499"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 transition-colors hover:border-white/20 hover:bg-white/[0.06]"
              >
                <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="text-muted-foreground block text-2xs uppercase tracking-label">
                    Talk to a human
                  </span>
                  <span className="block text-sm font-medium">
                    (858) 215-2499
                  </span>
                </span>
              </a>
              <p className="text-muted-foreground/70 pt-1 text-center text-2xs">
                Built for general contractors and their subs.
              </p>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
